import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../base/Button';

interface Article {
  id: string;
  title: string;
  content: string;
  category: string;
  excerpt: string;
  image_url: string;
  author: string;
  published_at: string;
  tags: string[];
  view_count: number;
}

interface NewsLayoutProps {
  articles: Article[];
  loading: boolean;
  category: string;
  categoryColor: string;
}

export default function NewsLayout({ articles, loading, category, categoryColor }: NewsLayoutProps) {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const articlesPerPage = 12;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1天前';
    if (diffDays < 7) return `${diffDays}天前`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}週前`;
    return date.toLocaleDateString('zh-TW');
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('zh-TW', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getCategoryFallbackImage = (category: string) => {
    const timestamp = Date.now();
    switch (category) {
      case '塔羅占卜':
        return `https://public.readdy.ai/ai/img_res/53c653d1c428bbfcc08ec7fe7a020b6c.jpg${timestamp}&orientation=landscape`;
      case '八字算命':
        return `https://readdy.ai/api/search-image?query=traditional%20chinese%20fortune%20telling%20scene%20with%20ancient%20books%2C%20calligraphy%20brushes%2C%20red%20and%20gold%20decorations%2C%20elegant%20chinese%20study%20room%2C%20warm%20lighting%2C%20cultural%20heritage%20atmosphere&width=800&height=450&seq=bazi-${timestamp}&orientation=landscape`;
      case '紫微斗數':
        return `https://readdy.ai/api/search-image?query=ancient%20chinese%20astrology%20chart%20with%20purple%20and%20gold%20celestial%20symbols%2C%20traditional%20star%20map%2C%20mystical%20cosmic%20background%2C%20elegant%20oriental%20design%2C%20professional%20photography&width=800&height=450&seq=ziwei-${timestamp}&orientation=landscape`;
      case '星座運勢':
        return `https://readdy.ai/api/search-image?query=beautiful%20zodiac%20constellation%20wheel%20in%20night%20sky%2C%20glowing%20stars%20and%20cosmic%20nebula%2C%20mystical%20universe%20background%2C%20professional%20astronomy%20photography%2C%20vibrant%20colors&width=800&height=450&seq=astro-${timestamp}&orientation=landscape`;
      default:
        return `https://readdy.ai/api/search-image?query=mystical%20fortune%20telling%20scene%20with%20candles%20and%20crystals%2C%20spiritual%20atmosphere%2C%20warm%20golden%20lighting%2C%20professional%20photography%2C%20elegant%20and%20mysterious&width=800&height=450&seq=default-${timestamp}&orientation=landscape`;
    }
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>, category: string) => {
    const target = e.target as HTMLImageElement;
    // 如果已经是备用图片还失败，使用纯色占位
    if (target.src.includes('readdy.ai')) {
      target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="450"%3E%3Crect width="800" height="450" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="24" fill="%239ca3af"%3E' + encodeURIComponent(category) + '%3C/text%3E%3C/svg%3E';
    } else {
      target.src = getCategoryFallbackImage(category);
    }
  };

  // 分頁邏輯
  const indexOfLastArticle = currentPage * articlesPerPage;
  const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
  const currentArticles = articles.slice(indexOfFirstArticle, indexOfLastArticle);
  const totalPages = Math.ceil(articles.length / articlesPerPage);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-gray-100 rounded-lg p-4">
                  <div className="h-48 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* 頁面標題 */}
        <div className="border-b-2 border-gray-100 pb-4 mb-8">
          <div className="flex items-center">
            <div className={`w-1 h-8 ${categoryColor} mr-4`}></div>
            <h1 className="text-3xl font-bold text-gray-900">{category}</h1>
            <span className="ml-4 text-sm text-gray-500">共 {articles.length} 篇文章</span>
          </div>
        </div>

        {articles.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
              <i className="ri-article-line text-2xl text-gray-400"></i>
            </div>
            <p className="text-gray-500 text-lg">暫無{category}文章</p>
            <p className="text-gray-400 text-sm mt-2">文章正在生成中，請稍後再來查看</p>
          </div>
        ) : (
          <>
            {/* 頭條文章 */}
            {currentArticles.length > 0 && (
              <div className="mb-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div 
                    className="group cursor-pointer"
                    onClick={() => navigate(`/news/${currentArticles[0].id}`)}
                  >
                    <div className="relative overflow-hidden rounded-lg">
                      <img 
                        src={currentArticles[0].image_url || getCategoryFallbackImage(currentArticles[0].category)} 
                        alt={currentArticles[0].title}
                        className="w-full h-80 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => handleImageError(e, currentArticles[0].category)}
                        loading="lazy"
                      />
                      <div className={`absolute top-4 left-4 ${categoryColor} text-white px-3 py-1 rounded text-sm font-medium`}>
                        {category}
                      </div>
                    </div>
                    <div className="mt-4">
                      <h2 className="text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2 mb-3">
                        {currentArticles[0].title}
                      </h2>
                      <p className="text-gray-600 leading-relaxed line-clamp-3 mb-4">
                        {currentArticles[0].excerpt}
                      </p>
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center space-x-4">
                          <span>{formatDate(currentArticles[0].published_at)}</span>
                          <span>{formatTime(currentArticles[0].published_at)}</span>
                          <span>作者：{currentArticles[0].author}</span>
                        </div>
                        <div className="flex items-center">
                          <i className="ri-eye-line mr-1"></i>
                          {currentArticles[0].view_count}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 右側文章列表 */}
                  <div className="space-y-6">
                    {currentArticles.slice(1, 4).map((article) => (
                      <div 
                        key={article.id}
                        className="group cursor-pointer flex gap-4 pb-6 border-b border-gray-100 last:border-b-0"
                        onClick={() => navigate(`/news/${article.id}`)}
                      >
                        <div className="flex-shrink-0">
                          <img 
                            src={article.image_url || getCategoryFallbackImage(article.category)} 
                            alt={article.title}
                            className="w-32 h-24 object-cover object-center"
                            onError={(e) => handleImageError(e, article.category)}
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2 mb-2">
                            {article.title}
                          </h3>
                          <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                            {article.excerpt}
                          </p>
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <div className="flex items-center space-x-3">
                              <span>{formatDate(article.published_at)}</span>
                              <span>{formatTime(article.published_at)}</span>
                            </div>
                            <div className="flex items-center">
                              <i className="ri-eye-line mr-1"></i>
                              {article.view_count}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* 其他文章網格 */}
            {currentArticles.length > 4 && (
              <div className="mb-12">
                <div className="border-b-2 border-gray-100 pb-4 mb-8">
                  <h2 className="text-xl font-bold text-gray-900">更多{category}文章</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {currentArticles.slice(4).map((article) => (
                    <div 
                      key={article.id}
                      className="group cursor-pointer bg-white border border-gray-100 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                      onClick={() => navigate(`/news/${article.id}`)}
                    >
                      <div className="relative">
                        <img 
                          src={article.image_url || getCategoryFallbackImage(article.category)} 
                          alt={article.title}
                          className="w-full h-40 object-cover object-center"
                          onError={(e) => handleImageError(e, article.category)}
                          loading="lazy"
                        />
                        <div className={`absolute top-3 left-3 ${categoryColor} text-white px-2 py-1 rounded text-xs`}>
                          {category}
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2 mb-2">
                          {article.title}
                        </h3>
                        <p className="text-gray-600 text-xs line-clamp-2 mb-3">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>{formatDate(article.published_at)}</span>
                          <div className="flex items-center">
                            <i className="ri-eye-line mr-1"></i>
                            {article.view_count}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 分頁 */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <i className="ri-arrow-left-line mr-1"></i>
                  上一頁
                </Button>
                
                <div className="flex space-x-1">
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`px-3 py-1 rounded text-sm transition-colors ${
                        currentPage === i + 1
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  下一頁
                  <i className="ri-arrow-right-line ml-1"></i>
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
